<script lang="ts">
  import { withUniqueKeys } from '../lib/unique-keys';

  /** One entry rendered inside {@link SuggestionsList}. */
  export interface SuggestionItem {
    /** Identity of the suggestion. Repeats across identical results, so
     *  `withUniqueKeys` disambiguates it before it becomes an `{#each}` key. */
    key: string;
    /** Main text (usually the location name). */
    label: string;
    /** Optional right-aligned badge (usually a type tag like "STOP" / "ADDR"). */
    badge?: string;
  }

  interface Properties {
    items: SuggestionItem[];
    activeIndex: number;
    onSelect: (index: number) => void;
    onHover?: (index: number) => void;
    /** Text to show when `items` is empty (e.g. "Start typing to search…"). */
    emptyLabel?: string;
  }

  const { items, activeIndex, onSelect, onHover, emptyLabel }: Properties = $props();

  /** Suggestions keyed for `{#each}`: result identity plus its occurrence. */
  const keyedItems = $derived(withUniqueKeys(items, (item) => item.key));
</script>

<!-- Absolute-positioned dropdown of suggestions. When items is empty,
     renders the `emptyLabel` message iff one was provided (otherwise
     nothing renders); the parent input owns keyboard navigation. -->
{#if items.length > 0}
  <ul class="suggestions" role="listbox">
    {#each keyedItems as entry, index (entry.key)}
      <li>
        <button
          type="button"
          role="option"
          aria-selected={index === activeIndex}
          class="suggestion-item"
          class:active={index === activeIndex}
          onclick={() => {
            onSelect(index);
          }}
          onmouseenter={() => onHover?.(index)}
        >
          <span class="suggestion-name">{entry.item.label}</span>
          {#if entry.item.badge}<span class="suggestion-type">{entry.item.badge}</span>{/if}
        </button>
      </li>
    {/each}
  </ul>
{:else if emptyLabel}
  <div class="suggestions suggestions-empty" role="status">{emptyLabel}</div>
{/if}

<style>
  .suggestions {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    margin-top: 2px;
    max-height: 150px;
    overflow-y: auto;
    z-index: 1000;
    list-style: none;
  }

  .suggestion-item {
    width: 100%;
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    text-align: left;
  }

  .suggestion-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .suggestion-type {
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-fg-muted);
  }

  .suggestions-empty {
    padding: 6px 8px;
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
    font-style: italic;
  }
</style>
